import { MaskedLine, FadeUp } from "@/components/motion";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 border-t border-[#DFE5DC] py-14" data-testid={`privacy-${id}`}>
    <FadeUp>
      <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0F1F1A] mb-8">{title}</h2>
    </FadeUp>
    <div className="grid gap-6 max-w-3xl">{children}</div>
  </section>
);

const P = ({ children }) => <p className="text-[#5C6B62] leading-relaxed text-sm md:text-base">{children}</p>;
const Li = ({ children }) => (
  <li className="flex gap-3 text-[#5C6B62] leading-relaxed text-sm md:text-base">
    <span className="h-1.5 w-1.5 rotate-45 bg-[#C9A227] shrink-0 mt-2.5" />
    <span>{children}</span>
  </li>
);

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <section className="px-6 pt-40 pb-16">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-8">
            <MaskedLine delay={0.1}>Legal & Transparency</MaskedLine>
          </p>
          <h1 className="font-display font-light text-5xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl text-[#0F1F1A]">
            <MaskedLine delay={0.25}>Privacy</MaskedLine>
            <MaskedLine delay={0.45}><span className="italic text-[#0C6B4F]">Policy.</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-8 max-w-xl">
            <span className="text-[#5C6B62] leading-relaxed">
              Last updated: August 2026. This policy explains exactly what Trusted Financial Hub collects, why, and the control you have over it.
            </span>
          </MaskedLine>
        </div>
      </section>

      <div className="px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          <Section id="overview" title="1. Overview">
            <P>
              Trusted Financial Hub is an independent comparison service covering personal loans, credit-building tools and credit health (monitoring and identity protection). When you use our site, submit a loan request, or join the VIP subscriber list, you trust us with your information. This policy describes how we honor that trust.
            </P>
          </Section>

          <Section id="collect" title="2. Information We Collect">
            <ul className="grid gap-3">
              <Li><strong className="text-[#0F1F1A]">Request details:</strong> name, email address, phone number, state, ZIP code, requested loan amount and the service you're interested in — only what you type into our forms.</Li>
              <Li><strong className="text-[#0F1F1A]">Subscription details:</strong> your name and email when you join the VIP list.</Li>
              <Li><strong className="text-[#0F1F1A]">Technical data:</strong> IP address, browser and device type, pages visited and outbound link clicks, used for security, fraud prevention and analytics.</Li>
            </ul>
            <P>We never ask for your Social Security number, bank credentials, or full credit report on this website.</P>
          </Section>

          <Section id="use" title="3. How We Use Your Information">
            <ul className="grid gap-3">
              <Li>To match your request with lenders or providers in our network whose criteria fit your profile.</Li>
              <Li>To respond to your questions and fulfill your requests.</Li>
              <Li>To send VIP list communications you asked for — offer alerts, rate drops and product launches.</Li>
              <Li>To keep the site secure: detecting abuse, blocking fraudulent traffic and measuring performance.</Li>
            </ul>
          </Section>

          <Section id="sharing" title="4. Who We Share It With">
            <P>
              Your request details are shared only with the lenders or service providers needed to fulfill your request. Each provider's own privacy policy governs how they handle your information afterward, and we encourage you to read them.
            </P>
            <P>
              We do not sell your personal information to data brokers. We may share limited data with service vendors (hosting, security, analytics) under contractual confidentiality, and where required by law.
            </P>
          </Section>

          <Section id="communications" title="5. Communication Consent">
            <P>
              When you submit a form and check the consent box, you authorize Trusted Financial Hub and the providers in our network to contact you about your request by email or phone, including automated means where permitted by law. Consent is not a condition of any purchase, and you may withdraw it at any time by emailing info@trustedfinancialhub.com with the subject line "Opt Out".
            </P>
          </Section>

          <Section id="cookies" title="6. Cookies & Click Tracking">
            <P>
              We use minimal technical data to remember your preferences and to record when outbound provider links are clicked (timestamp, IP address, browser). This measurement keeps the service free and helps us understand which comparisons are useful. We do not run third-party advertising trackers on this site.
            </P>
          </Section>

          <Section id="security" title="7. Data Security & Retention">
            <P>
              All form submissions travel over encrypted connections, and access to stored information is restricted to authorized personnel. We retain request data only as long as needed to fulfill your request and meet legal obligations, after which it is deleted or anonymized.
            </P>
          </Section>

          <Section id="rights" title="8. Your Rights">
            <ul className="grid gap-3">
              <Li><strong className="text-[#0F1F1A]">Access & copy:</strong> request a copy of the information we hold about you.</Li>
              <Li><strong className="text-[#0F1F1A]">Correction & deletion:</strong> ask us to correct or delete your information.</Li>
              <Li><strong className="text-[#0F1F1A]">Opt out:</strong> withdraw communication consent at any time.</Li>
              <Li><strong className="text-[#0F1F1A]">State rights:</strong> residents of California and other states with privacy laws may exercise additional rights under those laws; we honor verified requests regardless of your state.</Li>
            </ul>
            <P>To exercise any right, email info@trustedfinancialhub.com. We respond within 30 days.</P>
          </Section>

          <Section id="third-party" title="9. Third-Party Links">
            <P>
              Provider links on this site take you to websites we don't operate. Their content, terms and privacy practices are their own — review them before sharing information or accepting any offer.
            </P>
          </Section>

          <Section id="children" title="10. Children's Privacy">
            <P>
              This site is intended for adults 18 and older. We do not knowingly collect information from minors, and we delete any such information promptly if discovered.
            </P>
          </Section>

          <Section id="changes" title="11. Changes & Contact">
            <P>
              If we update this policy, the revised version will be posted here with a new "last updated" date. Questions about privacy? Email info@trustedfinancialhub.com — a real person will reply.
            </P>
          </Section>
        </div>
      </div>
    </div>
  );
}
